import { IconButton, LuIcon, Separator, stopPropagation, Text, toast, useAlertDialog } from "@overmap-ai/blocks"
import {
	type Asset,
	EMPTY_ARRAY,
	ERROR_MSG_UNEXPECTED,
	type Procedure,
	type ProcedureInitiative,
	type ProcedureStep,
	type ProcedureStepFieldValuesAttachment,
	type Stored,
} from "@overmap-ai/common"
import { selectLatestProcedureStepFieldsOfProcedureStep } from "@overmap-ai/core"
import { extractFilesAndPromisesFromFieldValues, type Field, type FieldValues } from "@overmap-ai/forms"
import * as RadixAccordian from "@radix-ui/react-accordion"
import { memo, useCallback } from "react"
import { v4 as uuid } from "uuid"

import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import {
	selectAttachmentsByProcedureStepFieldValues,
	selectLatestFieldValuesByProcedureOfProcedureInitiativeAndProcedureStep,
	selectProceduresByAssetOfProcedureInitiative,
} from "../../store/selectors"
import { useDeserializeFieldSchema } from "../Form/hooks"
import { ProcedureStepFieldsSubmitter } from "../Form/ProcedureStepFieldsSubmitter"
import { getAttachmentPayloadsFromFilesAndPromises, getFieldValuesAttachmentsByFieldId } from "../Form/utils"
import { ProcedureStepIcon } from "../Icons/ProcedureStepIcon"
import { loadingToast } from "../Toasts"

interface BulkAssetControlProcedureStepItemProps {
	procedureInitiative: Stored<ProcedureInitiative>
	assetIds: Asset["uuid"][]
	procedureStep: Stored<ProcedureStep>
}

export const BulkAssetControlProcedureStepItem = memo<BulkAssetControlProcedureStepItemProps>((props) => {
	const { procedureInitiative, assetIds, procedureStep } = props

	const openAlertDialog = useAlertDialog()
	const latestProcedureStepFields = useAppSelector(selectLatestProcedureStepFieldsOfProcedureStep(procedureStep.uuid))
	const proceduresByAsset = useShallowEqaulSelector(
		selectProceduresByAssetOfProcedureInitiative(procedureInitiative.uuid),
	)
	const latestProcedureStepFieldValuesByAsset = useShallowEqaulSelector(
		selectLatestFieldValuesByProcedureOfProcedureInitiativeAndProcedureStep(
			procedureInitiative.uuid,
			procedureStep.uuid,
		),
	)
	const attachmentsByProcedureStepFieldValues = useAppSelector(selectAttachmentsByProcedureStepFieldValues)
	const fieldSchema = useDeserializeFieldSchema(latestProcedureStepFields?.fields ?? EMPTY_ARRAY)

	const submitProcedureStepFields = useCallback(
		async (allValues: FieldValues) => {
			if (!latestProcedureStepFields?.uuid) return
			if (fieldSchema.areValuesBlank(allValues)) return

			const changedValues = fieldSchema.changedFieldValues(fieldSchema.blankValues(), allValues)

			const loadingToastId = uuid()

			loadingToast({
				id: loadingToastId,
				description: `Submitting procedure step values for ${assetIds.length} assets.`,
			})

			const [values, filesAndPromises] = extractFilesAndPromisesFromFieldValues(
				fieldSchema.getFields(),
				changedValues,
			)

			const fieldValuesByProcedure: Record<Procedure["uuid"], FieldValues> = {}
			const fieldValuesAttachmentsByFieldIdByProcedure: Record<
				Procedure["uuid"],
				Record<Field["id"], Stored<ProcedureStepFieldValuesAttachment>[]>
			> = {}

			for (const assetId of assetIds) {
				if (!(assetId in proceduresByAsset)) continue

				const procedure = proceduresByAsset[assetId]!

				const procedureStepFieldValues = latestProcedureStepFieldValuesByAsset[procedure.uuid]
				const attachments = attachmentsByProcedureStepFieldValues[procedureStepFieldValues?.uuid ?? ""] ?? []

				const fieldValues = fieldSchema.deserializeValues(procedureStepFieldValues?.values ?? {})
				const attachmentsByFieldId = getFieldValuesAttachmentsByFieldId(attachments)

				for (const fieldId in values) {
					if (fieldId in attachmentsByFieldId) delete attachmentsByFieldId[fieldId]
				}

				fieldValuesByProcedure[procedure.uuid] = { ...fieldValues, ...values }
				fieldValuesAttachmentsByFieldIdByProcedure[procedure.uuid] = attachmentsByFieldId
			}

			const [offlineProcedureStepFieldValues, promise] = sdk.procedureStepFieldValues.bulkAdd(
				Object.entries(fieldValuesByProcedure).map(([procedureId, values]) => ({
					procedure: procedureId,
					procedure_step: procedureStep.uuid,
					values: fieldSchema.serializeValues(values),
					fields_revision: latestProcedureStepFields.uuid,
					published_at: new Date().toISOString(),
				})),
			)

			void sdk.procedureStepFieldValuesAttachments.bulkAdd(
				offlineProcedureStepFieldValues.flatMap(({ uuid, procedure }) => {
					const attachmentsByFieldId = fieldValuesAttachmentsByFieldIdByProcedure[procedure] ?? []
					return getAttachmentPayloadsFromFilesAndPromises(uuid, filesAndPromises, []).concat(
						Object.entries(attachmentsByFieldId).flatMap(([fieldId, attachments]) =>
							attachments.map((attachment) => ({
								field_values: uuid,
								field_identifier: fieldId,
								file_sha1: attachment.file_sha1,
								file_extension: attachment.file_extension,
								file_name: attachment.file_name,
								file_type: attachment.file_type,
								object_url: attachment.object_url,
							})),
						),
					)
				}),
			)

			void promise
				.then(() => {
					toast.success({
						title: "Success",
						description: `Procedure step fields set on ${assetIds.length} assets.`,
					})
				})
				.catch((error) => {
					toast.error({
						title: "Failed",
						description: error.message ?? ERROR_MSG_UNEXPECTED,
					})
				})
				.finally(() => {
					toast.dismiss(loadingToastId)
				})
		},
		[
			latestProcedureStepFields?.uuid,
			assetIds,
			fieldSchema,
			proceduresByAsset,
			latestProcedureStepFieldValuesByAsset,
			procedureStep.uuid,
			attachmentsByProcedureStepFieldValues,
		],
	)

	const handleSubmitFieldValues = useCallback(
		(values: FieldValues) => {
			openAlertDialog({
				title: "Confirm mass fields submission?",
				description: `You are about to set fields on ${assetIds.length} assets. Are you sure?`,
				size: "sm",
				onAction: () => submitProcedureStepFields(values),
			})
		},
		[openAlertDialog, assetIds.length, submitProcedureStepFields],
	)

	return (
		<RadixAccordian.Item value={procedureStep.uuid} onClick={stopPropagation} asChild>
			<div className="hover:inset-ring-(--base-a7) flex flex-col gap-4 transition-[box-shadow]" role="listitem">
				<RadixAccordian.Trigger asChild>
					<div className="group flex w-full cursor-pointer items-center gap-2" tabIndex={0}>
						<div className="flex min-w-0 grow items-center gap-2">
							<ProcedureStepIcon procedureStepId={procedureStep.uuid} />
							<Text className="text-wrap" size="sm">
								{procedureStep.name}
							</Text>
						</div>

						<IconButton accentColor="base" size="sm" variant="ghost">
							<LuIcon
								className="group-data-[state='open']:rotate-90"
								icon={modelIconMapping.chevronRight}
							/>
						</IconButton>
					</div>
				</RadixAccordian.Trigger>

				<RadixAccordian.Content className="flex flex-col gap-4">
					{!!latestProcedureStepFields && (
						<>
							<Separator size="full" />
							<ProcedureStepFieldsSubmitter
								size="xs"
								procedureStepFields={latestProcedureStepFields}
								onSubmit={handleSubmitFieldValues}
								hideCancel
							/>
						</>
					)}
				</RadixAccordian.Content>
			</div>
		</RadixAccordian.Item>
	)
})

BulkAssetControlProcedureStepItem.displayName = "BulkAssetControlProcedureStepItem"
